import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Alert,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ACCOUNTING_API } from '../../../../config/api';
import { accountingApi } from '../../../../config/apiClient';
import { useAuth } from '../../../../contexts/AuthContext';
import AccountingDataTable from '../../shared/AccountingDataTable';
import type { DataTableColumn } from '../../shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../../shared/StatsCard';
import AddLoanModal from './AddLoanModal';
import LoanDetailModal from './LoanDetailModal';
import { REMINDER_SEARCH_QUERY } from '../../../../utils/reminderDeepLink';
import GuideButton from '../../../common/guide/GuideButton';

interface LoanRow {
  id: number;
  bankAccountId: number;
  bankAccountName?: string | null;
  bankName?: string | null;
  name: string;
  loanNumber?: string | null;
  principalAmount: number;
  totalPayback: number;
  totalInterest: number;
  netDisbursedAmount: number;
  currency: string;
  loanDate: string;
  installmentCount: number;
  status: string;
  notes?: string | null;
  paidInstallmentCount: number;
  totalInstallmentCount: number;
  paidTotal: number;
  remainingTotal: number;
  nextDueDate?: string | null;
  overdueCount: number;
}

/** Para birimine göre toplayıp "12.000,00 ₺ + 500,00 $" biçiminde döndürür */
const sumByCurrency = (items: { amount: number; currency: string }[], locale: string): string => {
  const totals: Record<string, number> = {};
  items.forEach((i) => {
    const cur = i.currency || 'TRY';
    totals[cur] = (totals[cur] || 0) + Number(i.amount || 0);
  });
  const entries = Object.entries(totals);
  if (entries.length === 0) return `0,00 ₺`;
  const sym = (c: string) => (c === 'TRY' ? '₺' : c === 'USD' ? '$' : c === 'EUR' ? '€' : c);
  return entries
    .map(([cur, total]) => `${total.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${sym(cur)}`)
    .join(' + ');
};

const Loans: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userDetails } = useAuth();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  // Liste state'leri (server-side pagination)
  const [loans, setLoans] = useState<LoanRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtreler (bildirimden ?q= ile gelebilir)
  const [searchInput, setSearchInput] = useState(() => searchParams.get(REMINDER_SEARCH_QUERY)?.trim() ?? '');
  const [search, setSearch] = useState(() => searchParams.get(REMINDER_SEARCH_QUERY)?.trim() ?? '');
  const [statusFilter, setStatusFilter] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const q = searchParams.get(REMINDER_SEARCH_QUERY)?.trim();
    if (q) {
      setSearchInput(q);
      setSearch(q);
      setPage(0);
    }
  }, [searchParams]);

  // Özet kartları
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summary, setSummary] = useState({
    remaining: '-',
    thisMonth: '-',
    overdue: '-',
    activeCount: 0,
    overdueCount: 0,
  });

  // Satır menüsü + modallar
  const [anchorElRow, setAnchorElRow] = useState<{ [key: number]: HTMLElement | null }>({});
  const [detailLoanId, setDetailLoanId] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LoanRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Düzenleme (ad / no / not)
  const [editTarget, setEditTarget] = useState<LoanRow | null>(null);
  const [editName, setEditName] = useState('');
  const [editLoanNumber, setEditLoanNumber] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const loadLoans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await accountingApi.get(
        ACCOUNTING_API.BANK_LOANS({
          page: page + 1,
          pageSize,
          status: statusFilter || undefined,
          search: search || undefined,
        })
      );
      setLoans(res.data?.data || []);
      setTotalCount(res.data?.totalCount || 0);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, search]);

  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      const [loansRes, upcomingRes] = await Promise.all([
        accountingApi.get(ACCOUNTING_API.BANK_LOANS({ page: 1, pageSize: 1000, status: 'ACTIVE' })),
        accountingApi.get(ACCOUNTING_API.BANK_LOAN_UPCOMING(31)),
      ]);
      const activeLoans: LoanRow[] = loansRes.data?.data || [];
      const upcoming: any[] = upcomingRes.data?.data || [];

      const now = new Date();
      const overdueItems = upcoming.filter((u) => u.daysToDue < 0);
      const thisMonthItems = upcoming.filter((u) => {
        const d = new Date(u.dueDate);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      });

      setSummary({
        remaining: sumByCurrency(activeLoans.map((l) => ({ amount: l.remainingTotal, currency: l.currency })), localeCode),
        thisMonth: sumByCurrency(thisMonthItems.map((u) => ({ amount: u.amount, currency: u.currency })), localeCode),
        overdue: sumByCurrency(overdueItems.map((u) => ({ amount: u.amount, currency: u.currency })), localeCode),
        activeCount: activeLoans.length,
        overdueCount: overdueItems.length,
      });
    } catch (err) {
      console.error('[Loans] Özet yüklenemedi:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, [localeCode]);

  useEffect(() => {
    loadLoans();
  }, [loadLoans]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Arama debounce (500ms)
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(0);
      setSearch(value.trim());
    }, 500);
  };

  const refreshAll = () => {
    loadLoans();
    loadSummary();
  };

  const fmt = (n: number, cur: string) =>
    `${Number(n || 0).toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur === 'TRY' ? '₺' : cur}`;
  const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(localeCode) : '-');

  const handleOpenDetail = (loan: LoanRow) => {
    setDetailLoanId(loan.id);
    setDetailOpen(true);
  };

  const handleOpenEdit = (loan: LoanRow) => {
    setEditTarget(loan);
    setEditName(loan.name || '');
    setEditLoanNumber(loan.loanNumber || '');
    setEditNotes(loan.notes || '');
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    try {
      setSaving(true);
      await accountingApi.post(ACCOUNTING_API.BANK_LOAN_UPDATE(editTarget.id), {
        Id: editTarget.id,
        Name: editName.trim() || null,
        LoanNumber: editLoanNumber.trim() || null,
        Notes: editNotes.trim() || null,
        UpdatedBy: userDetails?.email || 'Sistem',
      });
      setEditTarget(null);
      setSnackbar(tt('Kredi güncellendi', 'Loan updated'));
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await accountingApi.post(ACCOUNTING_API.BANK_LOAN_DELETE(deleteTarget.id), {
        DeletedBy: userDetails?.email || 'Sistem',
      });
      setDeleteTarget(null);
      setSnackbar(tt('Kredi silindi', 'Loan deleted'));
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const columns = useMemo<DataTableColumn<LoanRow>[]>(() => [
    {
      id: 'name',
      label: tt('Kredi Adı', 'Loan Name'),
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RequestQuoteIcon fontSize="small" color="primary" />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.name}</Typography>
            {row.loanNumber && (
              <Typography variant="caption" color="text.secondary">No: {row.loanNumber}</Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      id: 'bank',
      label: tt('Banka', 'Bank'),
      render: (row) => <>{row.bankName || row.bankAccountName || '-'}</>,
    },
    {
      id: 'principal',
      label: tt('Anapara', 'Principal'),
      align: 'right',
      render: (row) => <>{fmt(row.principalAmount, row.currency)}</>,
    },
    {
      id: 'payback',
      label: tt('Toplam Geri Ödeme', 'Total Payback'),
      align: 'right',
      render: (row) => <>{fmt(row.totalPayback, row.currency)}</>,
    },
    {
      id: 'paid',
      label: tt('Ödenen', 'Paid'),
      align: 'right',
      render: (row) => (
        <Box>
          <Typography variant="body2" color="success.main">{fmt(row.paidTotal, row.currency)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {row.paidInstallmentCount}/{row.totalInstallmentCount} {tt('taksit', 'inst.')}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'remaining',
      label: tt('Kalan', 'Remaining'),
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }} color={row.remainingTotal > 0 ? 'error.main' : 'success.main'}>
          {fmt(row.remainingTotal, row.currency)}
        </Typography>
      ),
    },
    {
      id: 'nextDue',
      label: tt('Sonraki Vade', 'Next Due'),
      render: (row) => {
        if (!row.nextDueDate) return <>-</>;
        const due = new Date(row.nextDueDate);
        const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
        if (dueDay < today) {
          const days = Math.floor((today.getTime() - dueDay.getTime()) / 86400000);
          return <Chip size="small" color="error" label={tt(`${fmtDate(row.nextDueDate)} — ${days} gün gecikti`, `${fmtDate(row.nextDueDate)} — ${days} day(s) late`)} />;
        }
        return <>{fmtDate(row.nextDueDate)}</>;
      },
    },
    {
      id: 'status',
      label: tt('Durum', 'Status'),
      align: 'center',
      render: (row) => (
        <Chip
          size="small"
          color={row.status === 'ACTIVE' ? 'primary' : row.status === 'CLOSED' ? 'success' : 'default'}
          label={row.status === 'ACTIVE' ? tt('Aktif', 'Active') : row.status === 'CLOSED' ? tt('Kapandı', 'Closed') : tt('İptal', 'Canceled')}
        />
      ),
    },
    {
      id: 'actions',
      label: tt('İşlemler', 'Actions'),
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorElRow((prev) => ({ ...prev, [row.id]: e.currentTarget })); }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorElRow[row.id]}
            open={Boolean(anchorElRow[row.id])}
            onClose={() => setAnchorElRow((prev) => ({ ...prev, [row.id]: null }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { setAnchorElRow((prev) => ({ ...prev, [row.id]: null })); handleOpenDetail(row); }}>
              <ListItemIcon><InfoIcon fontSize="small" color="info" /></ListItemIcon>
              <ListItemText primary={tt('Detay / Taksitler', 'Detail / Installments')} />
            </MenuItem>
            <MenuItem onClick={() => { setAnchorElRow((prev) => ({ ...prev, [row.id]: null })); handleOpenEdit(row); }}>
              <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText primary={tt('Düzenle', 'Edit')} />
            </MenuItem>
            <MenuItem onClick={() => { setAnchorElRow((prev) => ({ ...prev, [row.id]: null })); setDeleteTarget(row); }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText primary={tt('Sil', 'Delete')} />
            </MenuItem>
          </Menu>
        </Box>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [anchorElRow, localeCode, today]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Başlık */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/banka')} aria-label={tt('Geri', 'Back')}>
          <ArrowBackIcon />
        </IconButton>
        <RequestQuoteIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" component="h1" fontWeight="bold">
          {tt('Krediler', 'Loans')}
        </Typography>
        <GuideButton guideKey="finance.loans" />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={<PaymentsIcon />} onClick={() => navigate('/kredi-odemeleri')}>
          {tt('Kredi Ödemeleri', 'Loan Payments')}
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddModalOpen(true)}>
          {tt('Kredi Ekle', 'Add Loan')}
        </Button>
      </Box>

      {/* Özet kartları */}
      <StatsCardsContainer loading={summaryLoading} cardCount={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<AccountBalanceIcon />}
            color={STATS_COLORS.blue}
            title={summary.remaining}
            subtitle={tt('Toplam Kalan Borç', 'Total Remaining Debt')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<EventAvailableIcon />}
            color={STATS_COLORS.orange}
            title={summary.thisMonth}
            subtitle={tt('Bu Ay Ödenecek', 'Due This Month')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<EventBusyIcon />}
            color={STATS_COLORS.red}
            title={summary.overdue}
            subtitle={tt('Vadesi Geçmiş Toplam', 'Total Overdue')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<RequestQuoteIcon />}
            color={STATS_COLORS.teal}
            title={String(summary.activeCount)}
            subtitle={tt('Aktif Kredi Sayısı', 'Active Loan Count')}
          />
        </Grid>
      </StatsCardsContainer>

      {/* Vadesi geçmiş taksit banner'ı — bildirimden bağımsız, sayfada her zaman görünür */}
      {summary.overdueCount > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {tt(
            `Vadesi geçmiş ${summary.overdueCount} taksit var — toplam ${summary.overdue}. Lütfen en kısa sürede ödeme yapınız.`,
            `${summary.overdueCount} installment(s) overdue — total ${summary.overdue}. Please make the payment as soon as possible.`
          )}
        </Alert>
      )}

      {/* Filtreler */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder={tt('Kredi adı, no veya banka ara...', 'Search loan name, number or bank...')}
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="loan-status-filter-label">{tt('Durum', 'Status')}</InputLabel>
              <Select
                labelId="loan-status-filter-label"
                value={statusFilter}
                label={tt('Durum', 'Status')}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">{tt('Tümü', 'All')}</MenuItem>
                <MenuItem value="ACTIVE">{tt('Aktif', 'Active')}</MenuItem>
                <MenuItem value="CLOSED">{tt('Kapandı', 'Closed')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshAll}>
              {tt('Yenile', 'Refresh')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <AccountingDataTable<LoanRow>
        columns={columns}
        rows={loans}
        getRowId={(row) => `loan-${row.id}`}
        loading={loading}
        serverSidePagination
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={(newPage, newPageSize) => {
          setPage(newPage);
          setPageSize(newPageSize);
        }}
        onRowClick={(row) => handleOpenDetail(row)}
        emptyMessage={search || statusFilter ? tt('Filtreye uygun kredi bulunamadı', 'No loans match the filter') : tt('Henüz kredi eklenmemiş', 'No loans added yet')}
        emptyIcon={<RequestQuoteIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        ariaLabel={tt('Krediler tablosu', 'Loans table')}
        columnResizeStorageKey="bank-loans"
      />

      {/* Kredi ekleme */}
      <AddLoanModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => {
          setSnackbar(tt('Kredi eklendi', 'Loan added'));
          refreshAll();
        }}
      />

      {/* Detay */}
      <LoanDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        loanId={detailLoanId}
        onChanged={refreshAll}
      />

      {/* Düzenleme */}
      <Dialog open={Boolean(editTarget)} onClose={saving ? undefined : () => setEditTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('Krediyi Düzenle', 'Edit Loan')}</DialogTitle>
        <DialogContent>
          <TextField
            label={tt('Kredi Adı *', 'Loan Name *')}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            disabled={saving}
          />
          <TextField
            label={tt('Kredi/Sözleşme No', 'Loan/Contract No')}
            value={editLoanNumber}
            onChange={(e) => setEditLoanNumber(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            disabled={saving}
          />
          <TextField
            label={tt('Notlar', 'Notes')}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            disabled={saving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={saving}>{tt('İptal', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving || !editName.trim()} startIcon={saving ? <CircularProgress size={16} /> : undefined}>
            {tt('Kaydet', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme onayı */}
      <Dialog open={Boolean(deleteTarget)} onClose={deleting ? undefined : () => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('Krediyi Sil', 'Delete Loan')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {tt(
              `"${deleteTarget?.name}" kredisini silmek istediğinize emin misiniz? Ödenmiş taksiti olan krediler silinemez.`,
              `Are you sure you want to delete the loan "${deleteTarget?.name}"? Loans with paid installments cannot be deleted.`
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>{tt('İptal', 'Cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting} startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}>
            {tt('Sil', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSnackbar(null)}>{snackbar}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Loans;
